// IncidentBanner v0.3.1 — collapsible banner with persisted per-site preference.
// Uses the same headline/manual/test behavior as prior versions: highest-severity
// incident is the headline, manual/test modes are distinct, and optional BGP events
// render inline under the active incident.
import type { BgpRecentResponse, IncidentBlock } from '../lib/api.js';
import type { DashboardResource } from '../lib/dashboardResource.js';
import BgpTicker from './BgpTicker.js';
import CommandPanel from './ui/CommandPanel.js';
import { usePersistedPanelState } from '../hooks/usePersistedPanelState.js';
import { useId } from 'react';
import SectionHeader from './ui/SectionHeader.js';
import StatusGlow from './ui/StatusGlow.js';

const SEVERITY_LABEL: Record<number, string> = {
  1: 'DEGRADED',
  2: 'MAJOR OUTAGE',
  3: 'DOWN',
};

type BannerTone = 1 | 2 | 3 | 'manual';

function fmtDuration(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function toneFromMode(sev: number, isManual: boolean): BannerTone {
  if (isManual) return 'manual';
  if (sev >= 3) return 3;
  if (sev >= 2) return 2;
  if (sev >= 1) return 1;
  return 1;
}

interface Props {
  incident: DashboardResource<IncidentBlock | null>;
  bgp?: DashboardResource<BgpRecentResponse>;
}

export default function IncidentBanner({ incident, bgp }: Props) {
  if (!incident.ready && !incident.loading && !incident.error && !incident.data) return null;
  const activeIncident = incident.data;
  const bgpEvents = bgp?.data?.events ?? [];
  if (!activeIncident || !activeIncident.active || activeIncident.incidents.length === 0)
    return null;

  const sorted = [...activeIncident.incidents].sort((a, b) => b.severity - a.severity);
  const headline = sorted[0];
  if (!headline) return null;
  const others = sorted.slice(1);

  const isTest = activeIncident.is_test === true;
  const isManualHeadline = !isTest && headline.is_manual === true;

  let containerCls: string;
  let accentCls: string;
  let label: string;
  let indicatorMode: string;
  let pulseCls = 'animate-pulse';
  if (isTest) {
    containerCls = 'bg-[#3d3014] border-l-4 border-warn';
    accentCls = 'text-warn';
    label = 'TEST INCIDENT';
    indicatorMode = 'OPERATOR · DRILL';
  } else if (isManualHeadline) {
    containerCls = 'bg-[#3d2814] border-l-4 border-warn';
    accentCls = 'text-warn';
    label = 'MANUALLY FLAGGED';
    indicatorMode = 'OPERATOR · NO POLL';
    pulseCls = '';
  } else {
    containerCls = 'bg-[#3d1414] border-l-4 border-magenta';
    accentCls = 'text-magenta';
    label = 'INCIDENT MODE';
    indicatorMode = 'FAST-POLL · 10s';
  }

  const tone = toneFromMode(headline.severity, isTest || isManualHeadline);
  const severityLabel = SEVERITY_LABEL[headline.severity] ?? 'INCIDENT';
  const [expanded, setExpanded] = usePersistedPanelState('panel:incident-banner', true);
  const contentId = useId();

  return (
    <CommandPanel
      className={`${containerCls} sticky top-0 z-50 rounded-none border-b-0`}
      bodyClassName="space-y-1 pt-2 pb-2.5"
    >
      {isTest && (
        <div className="bg-warn text-bg text-center font-mono font-bold text-xs tracking-[3px] py-0.5">
          ⚠ TEST INCIDENT — DRILL ONLY · NOT A REAL OUTAGE ⚠
        </div>
      )}
      {isManualHeadline && (
        <div className="bg-warn text-bg text-center font-mono font-bold text-xs tracking-[3px] py-0.5">
          ⚠ MANUALLY FLAGGED — UPSTREAM STATUS PAGE DISPUTED ⚠
        </div>
      )}
      <SectionHeader
        title={
          <span className="flex items-center gap-2 min-w-0">
            <StatusGlow severity={tone} className={pulseCls}>
              ⚠
            </StatusGlow>
            <span className={`uppercase tracking-[2px] text-xs ${accentCls}`}>{label}</span>
            <span className="text-muted">·</span>
            <span className="text-text font-semibold truncate max-w-[24ch]">
              {headline.provider_name}
            </span>
            <StatusGlow severity={tone}>{severityLabel}</StatusGlow>
            {headline.message ? (
              <span className="text-muted text-xs truncate max-w-[30ch]">{headline.message}</span>
            ) : null}
          </span>
        }
        right={
          <span className="flex items-center gap-2 text-dim">
            <span>{indicatorMode}</span>
            <span className="tabular-nums">{fmtDuration(headline.duration_ms)}</span>
          </span>
        }
        expanded={expanded}
        onToggle={() => setExpanded((v) => !v)}
        contentId={contentId}
        compact
      />
      {expanded && (
        <>
          {isManualHeadline && headline.notice && (
            <div className="px-4 font-mono text-xs text-text">
              <span className="text-warn tracking-[1px]">NOTICE:</span>{' '}
              <span className="text-text">{headline.notice}</span>
            </div>
          )}
          {others.length > 0 && (
            <div className="px-4 font-mono text-xs text-muted flex flex-wrap gap-x-3">
              <span className="text-dim">also active:</span>
              {others.map((i) => {
                const otherTone = toneFromMode(i.severity, i.is_manual === true);
                const otherLabel = SEVERITY_LABEL[i.severity] ?? '?';
                return (
                  <span key={i.slug} className="flex items-center gap-2">
                    <span>{i.provider_name}</span>
                    <StatusGlow severity={otherTone}>{otherLabel}</StatusGlow>
                    {i.is_manual ? <span className="text-warn">manual</span> : null}
                  </span>
                );
              })}
            </div>
          )}
          {bgpEvents.length > 0 && <BgpTicker events={bgpEvents} />}
        </>
      )}
    </CommandPanel>
  );
}
